/**
 * VAVCI · Quick-view: modal de selección de variante para las cards del Home.
 *
 * Botón [data-vqv-open] (en vavci-products) → fetch /products/HANDLE?view=quickview
 * (caché en memoria) → inyecta el fragmento en un <dialog> modal nativo (focus-trap,
 * ESC y scroll-lock nativos) → galería + variante + cantidad + añadir/comprar.
 * Añadir reusa el camino AJAX de vavci-quick-add (CartLinesUpdateEvent); al cerrar
 * el <dialog>, el cart-drawer de Horizon hace su auto-open nativo (si está activado).
 */
import { CartLinesUpdateEvent } from '@shopify/events';
// La regla de «qué valores existen / tienen stock dada la selección» es la misma que en la
// PDP y ya está probada allí (scripts/__tests__/pdp-variants.test.js): se reutiliza en vez
// de reescribirla. Los scripts de deploy que suben este asset suben también ese módulo.
import { valueStates } from './vavci-pdp-variants.js';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const cache = new Map();

// Textos del shell (se pintan antes de tener HTML del servidor): los publica el
// snippet vavci-i18n como window.vavciI18n; con fallback en español por robustez.
// Los textos que dependen de la variante viajan como data-i18n-* en el fragmento.
const I18N = Object.assign(
  {
    close: 'Cerrar',
    loading: 'Cargando…',
    error: 'No se pudo cargar el producto.',
    viewProduct: 'Ver ficha completa',
    productOptions: 'Opciones del producto',
  },
  window.vavciI18n || {}
);

function esc(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

let dialog = null;
let contentEl = null;
let lastFocus = null;

function injectShellStyles() {
  if (document.getElementById('vqv-shell-styles')) return;
  const style = document.createElement('style');
  style.id = 'vqv-shell-styles';
  style.textContent = `
    .vqv-modal { width: min(960px, 92vw); max-width: 960px; max-height: 90vh; padding: 0; border: none; border-radius: 18px; background: var(--vc-bg, #FBF7F3); color: inherit; box-shadow: 0 30px 80px rgba(22,17,13,.32); overflow: hidden; }
    .vqv-modal::backdrop { background: rgba(22,17,13,.55); backdrop-filter: blur(2px); }
    .vqv-modal__scroll { max-height: 90vh; overflow-y: auto; padding: 28px clamp(20px, 4vw, 40px) 36px; }
    .vqv-modal__close { position: absolute; top: 16px; right: 16px; z-index: 5; width: 42px; height: 42px; border-radius: 50%; border: 1.5px solid rgba(22,17,13,.18); background: #fff; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; color: var(--vc-ink, #16110D); }
    .vqv-modal__close:hover { background: var(--vc-ink, #16110D); color: var(--vc-bg, #FBF7F3); border-color: var(--vc-ink, #16110D); }
    .vqv-modal__loading, .vqv-modal__error { padding: 64px 20px; text-align: center; font-family: var(--vc-sans, sans-serif); color: var(--vc-muted, #8a8079); }
    @media (max-width: 760px) {
      .vqv-modal { width: 100vw; max-width: 100vw; height: 100dvh; max-height: 100dvh; border-radius: 0; }
      .vqv-modal__scroll { max-height: 100dvh; padding: 64px 18px 40px; }
    }
    @media (prefers-reduced-motion: reduce) { .vqv-modal { transition: none; } }
  `;
  document.head.appendChild(style);
}

function buildShell() {
  if (dialog) return;
  injectShellStyles();
  dialog = document.createElement('dialog');
  dialog.className = 'vqv-modal';
  dialog.setAttribute('aria-label', I18N.productOptions);
  dialog.innerHTML =
    '<button type="button" class="vqv-modal__close" data-vqv-close aria-label="' + esc(I18N.close) + '">' +
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>' +
    '</button>' +
    '<div class="vqv-modal__scroll"><div class="vqv-modal__content" data-vqv-content></div></div>';
  document.body.appendChild(dialog);
  contentEl = dialog.querySelector('[data-vqv-content]');

  dialog.addEventListener('click', (e) => {
    if (e.target === dialog || e.target.closest('[data-vqv-close]')) close();
  });
  dialog.addEventListener('close', () => { if (lastFocus && lastFocus.focus) lastFocus.focus(); });
}

function close() {
  if (dialog && dialog.open) dialog.close();
}

async function open(baseUrl) {
  buildShell();
  lastFocus = document.activeElement;
  contentEl.innerHTML = '<div class="vqv-modal__loading">' + esc(I18N.loading) + '</div>';
  if (!dialog.open) dialog.showModal();

  const url = baseUrl + (baseUrl.indexOf('?') === -1 ? '?' : '&') + 'view=quickview';
  try {
    let html = cache.get(url);
    if (!html) {
      const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      html = await res.text();
      cache.set(url, html);
    }
    contentEl.innerHTML = html;
    initFragment(contentEl);
    const closeBtn = dialog.querySelector('[data-vqv-close]');
    if (closeBtn) closeBtn.focus();
  } catch (err) {
    const ficha = baseUrl.split('?')[0];
    contentEl.innerHTML = '<div class="vqv-modal__error">' + esc(I18N.error) + ' <a href="' + ficha + '">' + esc(I18N.viewProduct) + '</a></div>';
  }
}

function initFragment(root) {
  const data = root.querySelector('[data-vqv-variants]');
  let variants = [];
  try { variants = JSON.parse((data && data.textContent) || '[]'); } catch (e) { variants = []; }
  root._vqvVariants = variants;
  root._vqvSelected = Array.prototype.map.call(
    root.querySelectorAll('[data-vqv-option]'),
    (opt) => { const a = opt.querySelector('.vqv__pill.is-active'); return a ? a.getAttribute('data-value') : null; }
  );
  root._vqvOptionValues = Array.prototype.map.call(
    root.querySelectorAll('[data-vqv-option]'),
    (opt) => Array.prototype.map.call(opt.querySelectorAll('[data-vqv-pill]'), (p) => p.getAttribute('data-value'))
  );
  paintPillStates(root);

  root.addEventListener('click', onFragmentClick);
  root.addEventListener('input', onFragmentInput);
  root.addEventListener('submit', onFragmentSubmit);
}

function onFragmentClick(e) {
  const root = contentEl;
  const thumb = e.target.closest('[data-vqv-thumb]');
  if (thumb) {
    setMainImage(root, thumb.getAttribute('data-full'));
    root.querySelectorAll('[data-vqv-thumb]').forEach((t) => t.classList.toggle('is-active', t === thumb));
    return;
  }
  const pill = e.target.closest('[data-vqv-pill]');
  if (pill) { selectPill(root, pill); return; }

  const qtyBtn = e.target.closest('[data-vqv-qty]');
  if (qtyBtn) { stepQty(root, Number(qtyBtn.getAttribute('data-vqv-qty'))); return; }

  const buy = e.target.closest('[data-vqv-buy]');
  if (buy) { e.preventDefault(); buyNow(root); return; }
}

function onFragmentInput(e) {
  if (!e.target.closest('[data-vqv-qty-input]')) return;
  const input = e.target;
  const n = parseInt(input.value, 10);
  if (!n || n < 1) input.value = 1;
}

function selectPill(root, pill) {
  const group = pill.closest('[data-vqv-option]');
  const idx = Number(group.getAttribute('data-option-index'));
  group.querySelectorAll('[data-vqv-pill]').forEach((pp) => pp.classList.toggle('is-active', pp === pill));
  const cur = group.querySelector('[data-vqv-opt-current]');
  if (cur) cur.textContent = pill.getAttribute('data-value');
  root._vqvSelected[idx] = pill.getAttribute('data-value');
  paintPillStates(root);
  updateVariant(root);
}

/**
 * Marca como agotada (.is-out) cada píldora cuyo valor no tiene stock en la rama elegida.
 * Antes el único aviso de «Agotado» estaba en el botón de abajo, y el cliente pulsaba
 * tono tras tono sin saber cuáles podía comprar. La píldora sigue siendo pulsable a
 * propósito: el cliente tiene que poder ver que ese tono existe aunque hoy no haya.
 */
function paintPillStates(root) {
  const variants = root._vqvVariants || [];
  const optionValues = root._vqvOptionValues || [];
  const selection = root._vqvSelected || [];
  if (!variants.length) return;
  root.querySelectorAll('[data-vqv-option]').forEach((group) => {
    const position = Number(group.getAttribute('data-option-index'));
    const estados = new Map(valueStates({ variants, optionValues, selection, position }).map((e) => [e.value, e]));
    group.querySelectorAll('[data-vqv-pill]').forEach((pill) => {
      const st = estados.get(pill.getAttribute('data-value'));
      pill.classList.toggle('is-out', Boolean(st && !st.available));
    });
  });
}

function updateVariant(root) {
  const sel = root._vqvSelected;
  const v = root._vqvVariants.find((x) => x.options.every((o, i) => o === sel[i]));
  const idInput = root.querySelector('[data-vqv-id]');
  const addBtn = root.querySelector('[data-vqv-add]');
  const buyBtn = root.querySelector('[data-vqv-buy]');
  const priceEl = root.querySelector('[data-vqv-price]');
  const compEl = root.querySelector('[data-vqv-compare]');
  const stockEl = root.querySelector('[data-vqv-stock]');

  if (!v) {
    if (addBtn) { addBtn.disabled = true; addBtn.textContent = addBtn.dataset.i18nUnavailable || 'Combinación no disponible'; }
    if (buyBtn) buyBtn.disabled = true;
    return;
  }
  if (idInput) idInput.value = v.id;
  if (priceEl) priceEl.textContent = v.price;
  if (compEl) {
    if (v.compare_at_price) { compEl.hidden = false; compEl.textContent = v.compare_at_price; }
    else { compEl.hidden = true; compEl.textContent = ''; }
  }
  if (v.featured_media) setMainImage(root, v.featured_media);
  updateStock(stockEl, v);
  if (addBtn) {
    addBtn.disabled = !v.available;
    addBtn.textContent = v.available
      ? (addBtn.dataset.i18nAdd || 'Añadir al carrito')
      : (addBtn.dataset.i18nSoldOut || 'Agotado');
  }
  if (buyBtn) buyBtn.disabled = !v.available;
}

function updateStock(el, v) {
  if (!el) return;
  const thr = Number(el.getAttribute('data-threshold') || 0);
  if (!v.available) { el.textContent = el.dataset.i18nSoldOut || 'Agotado'; el.className = 'vqv__stock is-out'; return; }
  if (v.inventory_management && thr && v.inventory_quantity > 0 && v.inventory_quantity <= thr) {
    // La plantilla llega con ### como hueco del número (ver vavci-quick-view.liquid).
    el.textContent = (el.dataset.i18nLowStock || 'Quedan ### unidades').replace('###', v.inventory_quantity);
    el.className = 'vqv__stock is-low';
  } else {
    el.textContent = el.dataset.i18nInStock || 'En stock';
    el.className = 'vqv__stock is-in';
  }
}

function setMainImage(root, src) {
  const main = root.querySelector('[data-vqv-main]');
  if (!main || !src) return;
  if (main.tagName === 'IMG') { main.src = src; }
}

function stepQty(root, delta) {
  const input = root.querySelector('[data-vqv-qty-input]');
  if (!input) return;
  const next = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
  input.value = next;
}

// Prefetch ligero al pasar el ratón (perf): calienta la caché.
function prefetch(baseUrl) {
  const url = baseUrl + (baseUrl.indexOf('?') === -1 ? '?' : '&') + 'view=quickview';
  if (cache.has(url)) return;
  fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    .then((r) => (r.ok ? r.text() : null))
    .then((t) => { if (t) cache.set(url, t); })
    .catch(() => {});
}

document.addEventListener('click', (e) => {
  const opener = e.target.closest('[data-vqv-open]');
  if (!opener) return;
  e.preventDefault();
  const base = opener.getAttribute('data-product-url');
  if (base) open(base);
});

document.addEventListener('pointerenter', (e) => {
  const opener = e.target.closest && e.target.closest('[data-vqv-open]');
  if (opener && opener.getAttribute('data-product-url')) prefetch(opener.getAttribute('data-product-url'));
}, true);

function onFragmentSubmit(e) {
  const form = e.target.closest('[data-vqv-form]');
  if (!form) return;
  e.preventDefault();
  addToCart(form);
}

async function addToCart(form) {
  const btn = form.querySelector('[data-vqv-add]');
  if (btn && btn.getAttribute('aria-busy') === 'true') return;
  if (btn) btn.setAttribute('aria-busy', 'true');

  const fd = new FormData(form);
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });
  if (sectionIds.size) fd.set('sections', [...sectionIds].join(','));

  const qty = Number(fd.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  // Evento optimista: el badge del carrito sube al instante (cart-icon.js lo escucha).
  form.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: String(fd.get('id')), quantity: qty }],
    promise: deferred.promise,
  }));

  try {
    const res = await fetch(form.action, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd,
    });
    const data = await res.json();
    if (data.status) { // error (p. ej. sin stock)
      deferred.resolve({ detail: { didError: true, source: 'vavci-quick-view' } });
      return;
    }
    const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'vavci-quick-view', itemCount: qty, sections: data.sections, didError: false },
    });
    // Cerrar el <dialog>: Horizon difiere su auto-open del cart-drawer hasta que el modal cierra.
    close();
  } catch (err) {
    if (typeof deferred.reject === 'function') deferred.reject(err);
    console.warn('[vavci-quick-view] add to cart failed:', err);
  } finally {
    if (btn) btn.removeAttribute('aria-busy');
  }
}

async function buyNow(root) {
  const form = root.querySelector('[data-vqv-form]');
  if (!form) return;
  const addBtn = form.querySelector('[data-vqv-add]');
  if (addBtn && addBtn.disabled) return; // combinación no disponible/agotada
  const idInput = form.querySelector('[data-vqv-id]');
  const qtyInput = form.querySelector('[data-vqv-qty-input]');
  const id = idInput ? idInput.value : null;
  const qty = qtyInput ? (Number(qtyInput.value) || 1) : 1;
  if (!id) { window.location.href = `${ROOT}cart`; return; }
  try {
    const res = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, quantity: qty }),
    });
    if (!res.ok) throw new Error('add failed');
    window.location.href = `${ROOT}checkout`;
  } catch (err) {
    window.location.href = `${ROOT}cart`;
  }
}
